import ApiService, { IRequestParameters } from '@/plugins/api-plugin/services/api.service'
import {
    SupplierMessageDownloadFileDao,
    SupplierMessageFilterOptionsDao,
    SupplierMessageFixAllDao,
    SupplierMessageLogDao,
    SupplierMessageStatusChangeDao,
    SupplierMessageStatusDao,
    SupplierMessageStatusInfoDao,
    UpdateSupplierMessageConfigDto,
} from '@/interfaces/admin/SupplierMessage.dao'
import { PageNumberPagination } from '@/interfaces/common/PageNumberPagination'

export default class SupplierMessagesService extends ApiService {
    async getSupplierMessageStatuses(
        params?: IRequestParameters,
    ): Promise<[SupplierMessageStatusDao[], PageNumberPagination] | null> {
        return this.request<undefined, [SupplierMessageStatusDao[], PageNumberPagination] | null>(
            'GET',
            '/supplier-messages',
            params,
            undefined,
            undefined,
            'getSupplierMessageStatuses',
        ).catch(() => null)
    }

    async getSupplierMessageFilterOptions(): Promise<SupplierMessageFilterOptionsDao | null> {
        return this.request<undefined, SupplierMessageFilterOptionsDao | null>(
            'GET',
            '/supplier-messages/filter-options',
            undefined,
            undefined,
            undefined,
            'getSupplierMessageFilterOptions',
        ).catch(() => null)
    }

    async getSupplierMessageStatusInfo(id: number): Promise<SupplierMessageStatusInfoDao | null> {
        return this.request<undefined, SupplierMessageStatusInfoDao | null>(
            'GET',
            `/supplier-messages/${id}`,
            undefined,
            undefined,
            undefined,
            'getSupplierMessageStatusInfo',
        ).catch(() => null)
    }

    /**
     * Lists the logs of one message configuration. The configuration id travels inside params
     * (as configurationId) so the method stays compatible with useDataTableServer's
     * single-argument api[service][call](params) invocation.
     */
    async getSupplierMessageLogs(
        params?: IRequestParameters,
    ): Promise<[SupplierMessageLogDao[], PageNumberPagination] | null> {
        const { configurationId, ...rest } = params ?? {}
        return this.request<undefined, [SupplierMessageLogDao[], PageNumberPagination] | null>(
            'GET',
            `/supplier-messages/${configurationId}/logs`,
            rest,
            undefined,
            undefined,
            'getSupplierMessageLogs',
        ).catch(() => null)
    }

    async downloadSupplierMessageFile(logId: number): Promise<SupplierMessageDownloadFileDao | null> {
        return this.request<undefined, SupplierMessageDownloadFileDao | null>(
            'GET',
            `/supplier-messages/logs/${logId}/file`,
            undefined,
            undefined,
            undefined,
            'downloadSupplierMessageFile',
        ).catch(() => null)
    }

    async fixSupplierMessageLog(logId: number): Promise<SupplierMessageStatusChangeDao | null> {
        return this.request<undefined, SupplierMessageStatusChangeDao | null>(
            'POST',
            `/supplier-messages/logs/${logId}/fix`,
            undefined,
            undefined,
            undefined,
            'fixSupplierMessageLog',
        ).catch(() => null)
    }

    async fixAllSupplierMessageLogs(id: number): Promise<SupplierMessageFixAllDao | null> {
        return this.request<undefined, SupplierMessageFixAllDao | null>(
            'POST',
            `/supplier-messages/${id}/fix-all`,
            undefined,
            undefined,
            undefined,
            'fixAllSupplierMessageLogs',
        ).catch(() => null)
    }

    async updateSupplierMessageConfig(
        id: number,
        dto: UpdateSupplierMessageConfigDto,
    ): Promise<SupplierMessageStatusInfoDao | null> {
        return this.request<UpdateSupplierMessageConfigDto, SupplierMessageStatusInfoDao | null>(
            'PATCH',
            `/supplier-messages/${id}`,
            undefined,
            dto,
            undefined,
            'updateSupplierMessageConfig',
        ).catch(() => null)
    }
}
